import { useState, useEffect, useRef } from "react";
import { Accordion, Card, Table, Button, Row, Col, Form, Modal, Pagination, ListGroup } from "react-bootstrap";
//import axios from "axios";
import { MapContainer, TileLayer, GeoJSON, Pane } from "react-leaflet";
import { Poligono } from "../types/Poligono";
import { ContactosResumen } from "../types/ContactosResumen";
import { ContactosDetalle } from "../types/ContactosDetalle";
import { Tooltip } from "react-leaflet";
import L from "leaflet";
import { cleanGeometry, MapRefSetter, toFeatureCollection }  from "../utils/Geo";
import { ContactosDetalleResponse } from "../types/ContactosDetalleResponse";
import { Contact } from "../types/Contact";
import { Provincia } from "../types/Catalogos";
import { formatNumber, getInitials } from "../utils/utils";
import { exportToExcel } from "../services/exportToExcel";
import { GeoCluster } from "../types/GeoCluster";
import { api } from '../services/api';
import { useUser } from "../context/UserContext";
import { User } from "../types/User";
import { ExportListmonkRequest, ListaListmonk } from "../types/Listmonk";

const API_URL = process.env.REACT_APP_API_URL;

export default function ClusterPage() {
  const { user } = useUser();

  //console.log(user);

  // -----------------------------
  // 1. SELECTOR DE PROVINCIAS
  // -----------------------------  
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [loadingProvincias, setLoadingProvincias] = useState(true);

  useEffect(() => {
      const fetchProvincias = async () => {
          try {
              setLoadingProvincias(true);
              
              const res = await api.get<Provincia[]>(`${API_URL}/catalogos/provincias`);
              
              setProvincias(res.data);        // ← res.data, no res
          } catch (error) {
              console.error("Error al cargar provincias:", error);
              setProvincias([]);
          } finally {
              setLoadingProvincias(false);
          }
      };

      fetchProvincias();
  }, []);   // ← Array vacío para que se ejecute solo una vez

  const toggleProvincia = (codprov: string) => {
    setProvinciasSeleccionadas(prev =>
      prev.includes(codprov)
        ? prev.filter(p => p !== codprov)
        : [...prev, codprov]
    );
  };

  // Filtros adicionales
  const [rangoSueldoMin, setRangoSueldoMin] = useState<number | "">("");
  const [rangoSueldoMax, setRangoSueldoMax] = useState<number | "">("");
  const [sexosSeleccionados, setSexosSeleccionados] = useState<string[]>(["M", "F"]); // ["M", "F"]

  // -----------------------------
  // 2. POLÍGONOS
  // -----------------------------  
  const [poligonos, setPoligonos] = useState<Poligono[]>([]);
  const [loadingPoligonos, setLoadingPoligonos] = useState(false);
  const [provinciasSeleccionadas, setProvinciasSeleccionadas] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    //if (provinciasSeleccionadas.length === 0) return;

    const fetchPoligonos = async () => {
      if (provinciasSeleccionadas.length === 0) {
        setPoligonos([]);
        setLoadingPoligonos(false);
        setSelectedZones([]);           // Limpiar zonas seleccionadas
        setResumen([]);                 // Limpiar resumen        
        return;
      }

      setLoadingPoligonos(true);

      try {
        const params: any = {
          codprov: provinciasSeleccionadas.join(",") || undefined,
        };

        // Solo agregar filtros si tienen valor
        if (rangoSueldoMin !== "") params.sueldo_min = rangoSueldoMin;
        if (rangoSueldoMax !== "") params.sueldo_max = rangoSueldoMax;
        if (sexosSeleccionados.length > 0) params.sexo = sexosSeleccionados.join(",");

        const res = await api.get<Poligono[]>(`${API_URL}/geo/poligonos`, { params });     
        setPoligonos(res.data);
      } catch (error) {
        console.error("Error al cargar polígonos:", error);
        setPoligonos([]);
      } finally {
        setLoadingPoligonos(false);
      }
    };

    fetchPoligonos();
  }, [provinciasSeleccionadas, rangoSueldoMin, rangoSueldoMax, sexosSeleccionados]);

  // -----------------------------
  // 3. SELECCIÓN MÚLTIPLE DE CLUSTERS
  // -----------------------------
  //const [selected, setSelected] = useState<string[]>([]);
  const [selectedZones, setSelectedZones] = useState<string[]>([]); // formato: "codzona-codparroquia"
  const [parroquia, setParroquia] = useState("090112");

  const toggleSelect = (codzona: string, codparroquia: string) => {
    const key = `${codparroquia}-${codzona}`;

    setSelectedZones((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );

    //setParroquia(codparroquia); // opcional, si aún lo necesitas
  };

  //
  // Actualizar estado de clusters
  //
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    type: 'toggle' | 'delete';
    clusterId?: number;
    clusterName?: string;
    message: string;
  } | null>(null);

  interface ToggleResponse {
    ok: boolean;
    activo: boolean;
    message: string;
  }

  const toggleClusterStatus = (clusterId: number, clusterName: string) => {
    setConfirmAction({
      type: 'toggle',
      clusterId,
      clusterName,
      message: `¿Estás seguro de ${clusterName ? 'desactivar' : 'activar'} el cluster "${clusterName}"?`
    });
    setShowConfirmModal(true);
  };

  //
  //
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (poligonos.length === 0) return;

    const fc = toFeatureCollection(poligonos);
    const bounds = L.geoJSON(fc).getBounds();
    mapRef.current.fitBounds(bounds, { padding: [20, 20] });
  }, [poligonos]);


  // -----------------------------
  // 4. RESUMEN DE CONTACTOS
  // -----------------------------
  const [resumen, setResumen] = useState<ContactosResumen[]>([]);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [pageRes, setPageRes] = useState(1);
  const pageSizeRes = 10;

  useEffect(() => {
    if (selectedZones.length === 0) {
      setResumen([]);
      return;
    }
    
    const fetchResumen = async () => {
      setLoadingResumen(true);

      const itemsParam = selectedZones.join(",");   // Ej: "339-090112,340-090112,450-090113"

      // const res = await axios.get<ContactosResumen[]>(`${API_URL}/geo/resumen-contactos`, {
      //   params: {
      //     items: itemsParam,
      //     page: pageRes,
      //     page_size: pageSizeRes
      //   }
      // });

      const params: any = {
        items: itemsParam,
        page: pageRes,
        page_size: pageSizeRes,
      };

      // Agregar filtros adicionales
      if (rangoSueldoMin !== "") params.sueldo_min = rangoSueldoMin;
      if (rangoSueldoMax !== "") params.sueldo_max = rangoSueldoMax;
      if (sexosSeleccionados.length > 0) params.sexo = sexosSeleccionados.join(",");

      const res = await api.get<ContactosResumen[]>(`${API_URL}/geo/resumen-contactos`, {
        params
      });

      setResumen(res.data);
      setLoadingResumen(false);
    };

    fetchResumen();
  }, [selectedZones, pageRes, rangoSueldoMin, rangoSueldoMax, sexosSeleccionados]);

  // -----------------------------
  // 5. DETALLE DE CONTACTOS
  // -----------------------------
  const [detalle, setDetalle] = useState<ContactosDetalle[]>([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [pageDet, setPageDet] = useState(1);
  const [totalDetalle, setTotalDetalle] = useState(0);
  const pageSizeDet = 10;

  const [clusterSeleccionadoTabla, setClusterSeleccionadoTabla] = useState<{
    id: number;
    codzona: number;
    codparroquia: number;
  } | null>(null);

  useEffect(() => {
    console.log( "consultando detalle 0")
    if (!clusterSeleccionadoTabla) return;

    if (selectedZones.length === 0) {
      setDetalle([]);
      setTotalDetalle(0);
      return;
    }

    const fetchDetalle = async () => {
      if (!clusterSeleccionadoTabla) return;

      setLoadingDetalle(true);
      
      try {
        const params: any = {
          codparroquia: clusterSeleccionadoTabla.codparroquia,
          codzona: clusterSeleccionadoTabla.codzona,     // ← Nota: el backend espera "zonas", pero como es uno solo lo manejamos
          skip: (pageDet - 1) * pageSizeDet,
          limit: pageSizeDet
        };

        // === Agregar filtros ===
        if (rangoSueldoMin !== "") params.sueldo_min = rangoSueldoMin;
        if (rangoSueldoMax !== "") params.sueldo_max = rangoSueldoMax;

        if (sexosSeleccionados.length > 0) {
          params.sexo = sexosSeleccionados.join(",");
        }

        const res = await api.get<ContactosDetalleResponse>(`${API_URL}/geo/detalle-contactos-cluster`, 
          { params }
        );

        // Nueva estructura de respuesta
        setDetalle(res.data.items || []);
        setTotalDetalle(res.data.total || 0);
      } catch (error) {
        console.error("Error al cargar detalle:", error);
        setDetalle([]);
        setTotalDetalle(0);
      } finally {
        setLoadingDetalle(false);
      }
    };

    fetchDetalle();
  }, [clusterSeleccionadoTabla, pageDet, rangoSueldoMin, rangoSueldoMax, sexosSeleccionados]);


  //
  //
  const limpiarSeleccion = () => {
    setSelectedZones([]);                 // limpia selección del mapa
    setClusterSeleccionadoTabla(null); // limpia selección de tabla
    setDetalle([]);                  // limpia detalle
    setTotalDetalle(0);

    // Nuevos filtros
    setProvinciasSeleccionadas([]);
    setRangoSueldoMin("");
    setRangoSueldoMax("");
    setSexosSeleccionados([]);    

    //
    setClusterActual(null);
  
  };


  //
  const totalPages = Math.ceil(totalDetalle / pageSizeRes);

  const getPaginationItems = () => {
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= 10) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (pageDet > 4) pages.push('ellipsis');

      const start = Math.max(2, pageDet - 2);
      const end = Math.min(totalPages - 1, pageDet + 2);

      for (let i = start; i <= end; i++) pages.push(i);

      if (pageDet < totalPages - 3) pages.push('ellipsis');
      pages.push(totalPages);
    }

    return pages;
  };

  //
  //
  const [showModal, setShowModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const handleDetails = async (id: number) => {
    try {
      //setLoadingResumen(true);
      const res = await api.get<Contact>(`${API_URL}/contacts/${id}`);      
      setSelectedContact(res.data);
      setShowModal(true);
    } catch (err) {
      console.error('Error al obtener detalles:', err);
    }
  };

  // función que reciba el total de contactos y devuelva un color.
  const getColorByDensity = (count: number) => {
    if (count > 2000) return "#8B0000";     // rojo oscuro
    if (count > 1000) return "#B22222";     // rojo
    if (count > 500) return "#FF4500";     // naranja fuerte
    if (count > 300)  return "#FFA500";     // naranja
    if (count > 100)  return "#FFD700";     // amarillo
    return "#87CEFA";                      // azul suave
  };

  //
  //
  // Componente auxiliar para crear un campo en una sola línea y compacto
  const RenderInlineField = ({ label, field, type = 'text', error, contact, setContact, required = false, readOnly = false }: 
    { label: string, field: keyof Contact, type?: string, error?: string, contact: Contact, setContact: React.Dispatch<React.SetStateAction<Contact | null>>, required?: boolean, readOnly?: boolean }
  ) => (
    <Form.Group as={Row} className="form-group-compact d-flex align-items-center">
      
      {/* Etiqueta (3 columnas, alineación a la derecha) */}
      <Form.Label column sm="3" className="small-label text-end">
        {label}
        {required && <span className="text-danger">*</span>}
      </Form.Label>
      
      {/* Campo de Entrada (9 columnas) */}
      <Col sm="9">
        <Form.Control
          type={type}
          value={contact[field] as string || ''}
          onChange={(e) =>
            setContact({ ...contact, [field]: e.target.value as string }) // Usando string más seguro
          }
          isInvalid={!!error}
          className="compact-input" // Clase para reducir tamaño
          readOnly={readOnly}
          plaintext={readOnly} // Para campos de solo lectura, usa plaintext para que se vea como texto
        />
        {error && (
          <Form.Control.Feedback type="invalid">
            {error}
          </Form.Control.Feedback>
        )}
      </Col>
    </Form.Group>
  );

  // --- FIN COMPONENTE AUXILIAR ---  

  //
  // Exportar a Excel  
  //
  const [loadingExport, setLoadingExport] = useState(false);

  const exportarTodosAExcel = async () => {
    if (selectedZones.length === 0) {
      alert("Selecciona al menos un cluster para exportar");
      return;
    }

    //setLoadingExport(true);

    try {
      const allContacts: any[] = [];

      // Recorrer todos los clusters seleccionados
      for (const zone of selectedZones) {
        const [codparroquia, codzona] = zone.split('-').map(String);

        const params: any = {
          codparroquia,
          codzona,
          skip: 0,
          limit: 100000 // límite alto para traer todo
        };

        // === Aplicar los mismos filtros actuales ===
        if (rangoSueldoMin !== "") params.sueldo_min = rangoSueldoMin;
        if (rangoSueldoMax !== "") params.sueldo_max = rangoSueldoMax;
        if (sexosSeleccionados.length > 0) {
          params.sexo = sexosSeleccionados.join(",");
        }

        // Obtener TODOS los contactos de este cluster (sin paginación)
        const res = await api.get<ContactosDetalleResponse>(
          `${API_URL}/geo/detalle-contactos-cluster`, 
          { params }
        );

        if (res.data.items && res.data.items.length > 0) {
          allContacts.push(...res.data.items);
        }
      }

      if (allContacts.length === 0) {
        alert("No se encontraron contactos con los filtros actuales");
        return;
      } 

      // Preparar datos para Excel
      const dataForExcel = allContacts.map(c => ({
        "Nombre": c.apenomafi || '',
        "Cédula": c.numafi || '',
        "Teléfono": c.celular || '',
        "Correo": c.email || '',
        "Empresa": c.nomempresa || '',
        "Ocupación": c.ocuafi || '',
        "Sueldo": c.sueldo || '',
        "Dirección": c.dirafi || '',
        "RUC Empresa": c.rucemp || '',
        "Dirección Empresa": c.dirsuc || '',
        "Tipo Empresa": c.tipoempresa || '',
        "Teléfono Empresa": c.telsuc || ''
      }));

      // Crear libro de Excel
      const fecha = new Date().toISOString().slice(0,10).replace(/-/g, '');
      exportToExcel(dataForExcel, `Contactos_Clusters_${fecha}.xlsx`);

    } catch (error) {
      console.error("Error al exportar:", error);
      alert("Ocurrió un error al exportar los datos");
    } finally {
      setLoadingExport(false);
    }
  };

  //
  // Dialogo Guardar
  //
  const [dialogGuardar, setDialogGuardar] = useState(false);
  const [nombreCluster, setNombreCluster] = useState("");

  const guardarCluster = async (isSaveAs = false) => {
    const nombreFinal = isSaveAs ? nombreCluster.trim() : (clusterActual?.nombre || nombreCluster.trim());

    if (!nombreFinal) {
      alert("Por favor ingresa un nombre para el cluster");
      return;
    }

    // if (!nombreCluster.trim()) {
    //   alert("Por favor ingresa un nombre para el cluster");
    //   return;
    // }

    try {
      const payload: any = {
        id: !isSaveAs && clusterActual ? clusterActual.id : undefined,
        //id: isEditMode && clusterActual ? clusterActual.id : undefined, // ← Para actualizar
        nombre: nombreCluster.trim(),
        descripcion: "", // puedes agregar un campo extra en el modal
        provincias: provinciasSeleccionadas,
        zonas: selectedZones,
        sueldo_min: rangoSueldoMin || null,
        sueldo_max: rangoSueldoMax || null,
        sexo: sexosSeleccionados.length > 0 ? sexosSeleccionados.join(",") : null
      };

      await api.post(`${API_URL}/geo/cluster/save`, payload);

      alert(isSaveAs || !clusterActual 
        ? "✅ Cluster guardado correctamente" 
        : "✅ Cluster actualizado correctamente");

      setDialogGuardar(false);
      setNombreCluster("");
      setIsEditMode(false);
      setClusterActual(null);
      
      limpiarSeleccion();

      //cargarListaClusters();

    } catch (error: any) {
      console.error("Error al guardar cluster:", error);
      
      const mensaje = error.response?.data?.detail || 
                    error.response?.data?.message || 
                    "Error al guardar el cluster en el servidor";
      
      alert("❌ " + mensaje);
    }
  };

  //
  // Abrir clusters guardados
  //
  const [listaClusters, setListaClusters] = useState<GeoCluster[]>([]);
  const [dialogAbrir, setDialogAbrir] = useState(false);
  const [clusterActual, setClusterActual] = useState<GeoCluster | null>(null); // Cluster que se está editando
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [mostrarInactivos, setMostrarInactivos] = useState(false);

  const cargarListaClusters = async () => {
    try {
      const res = await api.get<GeoCluster[]>(`${API_URL}/geo/cluster/list`, {
          params: { mostrar_inactivos: mostrarInactivos }
        }
      );
      setListaClusters(res.data);
      setDialogAbrir(true);
      //console.log(listaClusters);
    } catch (error: any) {
      console.error("Error al cargar clusters:", error);
      alert("Error al cargar la lista de clusters: " + 
            (error.response?.data?.detail || error.message));
    }
  };

  const abrirCluster = (c: any) => {
    try {
      setDialogAbrir(false);

      // Manejo seguro: si ya es objeto, usarlo directamente; si es string, parsear
      const provincias = typeof c.provincias === 'string' 
        ? JSON.parse(c.provincias) 
        : c.provincias || [];

      const zonas = typeof c.zonas === 'string' 
        ? JSON.parse(c.zonas) 
        : c.zonas || [];

      setProvinciasSeleccionadas(provincias);
      setSelectedZones(zonas);

      // === NUEVOS FILTROS ===
      setRangoSueldoMin(c.sueldo_min ?? "");
      setRangoSueldoMax(c.sueldo_max ?? "");
      
      if (c.sexo) {
        setSexosSeleccionados(c.sexo.split(",").map((s: string) => s.trim()));
      } else {
        setSexosSeleccionados([]);
      }

      // Opcional: cerrar diálogo y limpiar
      setClusterActual(c);
      setIsEditMode(true);
      setNombreCluster(c.nombre);   // Precargar el nombre

      // Forzar recarga del mapa
      // setTimeout(() => { ... }, 100);

    } catch (error) {
      console.error("Error al abrir cluster:", error);
      alert("Error al cargar el cluster. Formato de datos incorrecto.");
    }
  };  

  // -----------------------------
  // COMPARTIR CLUSTER
  // -----------------------------
  const [showShareModal, setShowShareModal] = useState(false);
  const [clusterToShare, setClusterToShare] = useState<GeoCluster | null>(null);
  const [usuarioDestinoId, setUsuarioDestinoId] = useState("");
  const [permisoSeleccionado, setPermisoSeleccionado] = useState<"view" | "edit">("edit");
  const [userSuggestions, setUserSuggestions] = useState<any[]>([]);
  const [searchUserTerm, setSearchUserTerm] = useState("");

  const abrirModalCompartir = (c: GeoCluster) => {
    setClusterToShare(c);
    setUsuarioDestinoId("");
    setShowShareModal(true);
  };

  // Compartir
  const compartirCluster = async () => {
    // if (!clusterToShare || !usuarioDestinoId.trim()) {
    //   alert("Ingresa el ID del usuario");
    //   return;
    // }

    if (!clusterToShare || !usuarioDestinoId) return;

    try {
      await api.post('/geo/cluster/share', {
        cluster_id: clusterToShare.id,
        //usuario_destino_id: parseInt(usuarioDestinoId.trim()),
        usuario_destino_id: parseInt(usuarioDestinoId),
        permiso: permisoSeleccionado
      });

      alert("✅ Cluster compartido correctamente");
      setShowShareModal(false);
      //setUsuarioDestinoId("");
      cargarListaClusters();   // recargar lista
    } catch (error: any) {
      alert("❌ " + (error.response?.data?.detail || error.message));
    }
  };

  // Quitar compartir
  const quitarCompartir = async (clusterId: number, usuarioId: number) => {
    //if (!confirm("¿Quitar acceso a este usuario?")) return;

    try {
      await api.delete(`/geo/cluster/share/${clusterId}/${usuarioId}`);
      alert("✅ Acceso removido");
      cargarListaClusters();
    } catch (error: any) {
      alert("❌ Error al quitar acceso");
    }
  };  

  //
  // Exportar contactos a Listmonk
  //
  const [showListmonk, setShowListmonk] = useState(false);
  const [listas, setListas] = useState<{ id: number; name: string }[]>([]);
  const [listaId, setListaId] = useState<number | "">("");
  const [exportando, setExportando] = useState(false);

  const abrirListmonk = async () => {
    if (selectedZones.length === 0) {
      alert("Selecciona al menos un cluster");
      return;
    }
    const res = await api.get<ListaListmonk[]>("/listmonk/lists");
    setListas(res.data || []);
    setShowListmonk(true);
  };

  const exportarAListmonk = async () => {
    if (!listaId) return;
    setExportando(true);
    try {
      const contactos: any[] = [];
      for (const zone of selectedZones) {
        const [codparroquia, codzona] = zone.split('-').map(String);

        const params: any = {
          codparroquia,
          codzona,
          skip: 0,
          limit: 100000 // límite alto para traer todo
        };

        // === Aplicar los mismos filtros actuales ===
        if (rangoSueldoMin !== "") params.sueldo_min = rangoSueldoMin;
        if (rangoSueldoMax !== "") params.sueldo_max = rangoSueldoMax;
        if (sexosSeleccionados.length > 0) {
          params.sexo = sexosSeleccionados.join(",");
        }
        
        const res = await api.get<ContactosDetalleResponse>("/geo/detalle-contactos-cluster", 
          { params}
        );

        contactos.push(...(res.data.items || []));
      }

      console.log(contactos);

      const payload = contactos
        .filter((c) => c.email)
        .map((c) => ({
          nombre: c.apenomafi || "",
          email: c.email,
          telefono: c.celular || "",
          ocupacion: c.ocuafi || "",
        }));

      const res = await api.post<ExportListmonkRequest>("/listmonk/export", {
        list_id: listaId,
        contactos: payload,
        preconfirm: true,
      });

      alert(
        `Enviados: ${res.data.enviados}. Sin email: ${res.data.omitidos_sin_email}. Errores: ${res.data.errores.length}`
      );
      setShowListmonk(false);
    } catch (e: any) {
      alert(e.response?.data?.detail || "Error al exportar a Listmonk");
    } finally {
      setExportando(false);
    }
  };  

  // -----------------------------
  // 6. RENDER
  // -----------------------------
  return (
    <div>
      <h2 className="mb-4">Contactos por Clusters</h2>

      {/* MAPA */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Mapa de Clusters</strong>
            {selectedZones.length > 0 && (
              <span className="ms-3 text-primary">
                {selectedZones.length} cluster{selectedZones.length > 1 ? 's' : ''} seleccionado{selectedZones.length > 1 ? 's' : ''}
              </span>
            )}
          </div>

          <div className="d-flex align-items-center gap-2">
            
            {/* Limpiar Selección */}
            <Button 
              variant="outline-danger" 
              size="sm"
              onClick={limpiarSeleccion}
              title="Limpiar Selección"
            >
              <i className="bi bi-file"></i>
            </Button>

            {/* Guardar / Actualizar */}
            <Button 
              variant="success" 
              size="sm"
              onClick={() => {
                if (clusterActual) {
                  guardarCluster(false);           // Actualizar directamente
                } else {
                  setIsEditMode(false);
                  setNombreCluster("");
                  setDialogGuardar(true);
                }
              }}
              title={clusterActual ? "Actualizar Cluster Actual" : "Guardar Selección"}
            >
              <i className="bi bi-save"></i>
            </Button>

            {/* Guardar como Nuevo */}
            {clusterActual && (
              <Button 
                variant="outline-success" 
                size="sm"
                onClick={() => {
                  setIsEditMode(false);
                  setNombreCluster("");
                  setDialogGuardar(true);
                }}
                title="Guardar como Nuevo"
              >
                <i className="bi bi-save2"></i>
              </Button>
            )}

            {/* Abrir Clusters Guardados */}
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={cargarListaClusters}
              title="Abrir Cluster Guardado"
            >
              <i className="bi bi-folder2-open"></i>
            </Button>

            {/* Exportar a excel */}
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={exportarTodosAExcel}
              //className="d-none" // d-none oculta el boton
              title="Exportar contactos a Excel"
            >
              <i className="bi bi-folder2-open"></i>
            </Button>

            {/* Ocultar/Mostrar Filtros */}
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              disabled={selectedZones.length === 0 || loadingExport}
              title={sidebarOpen ? "Ocultar Filtros" : "Mostrar Filtros"}
            >
              <i className={`bi ${sidebarOpen ? 'bi-chevron-left' : 'bi-chevron-right'}`}></i>
            </Button>

            {/* boton abrir / guardar Listmonk */}
            <Button 
              size="sm" 
              variant="outline-primary" 
              onClick={abrirListmonk} 
              title="Enviar a Listmonk"
            >
              <i className="bi bi-envelope-plus"></i>
            </Button>

          </div>
          
          {/* <div>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="me-2"
            >
              {sidebarOpen ? "Ocultar Filtros" : "Mostrar Filtros"}
            </Button>
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={limpiarSeleccion} 
              className="me-2"
            >
              Limpiar Selección
            </Button>
            <Button 
              variant="success" 
              size="sm" 
              onClick={exportarTodosAExcel}
              disabled={selectedZones.length === 0 || loadingExport}
              className="d-none me-2" // d-none oculta el boton
            >
              {loadingExport ? "Exportando..." : "📊 Exportar a Excel"}
            </Button>
            <Button
              variant="outline-primary"  
              size="sm" 
              className="me-2" 
              onClick={() => setDialogGuardar(true)}
            >
              Guardar cluster
            </Button>
            <Button 
              variant="outline-primary"             
              size="sm"             
              className="me-2" 
              onClick={() => cargarListaClusters()}
            >
              Abrir
            </Button>

            <div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>


            </div>
          </div> */}
        </Card.Header>

        <div style={{ display: "flex", width: "100%" }}>
          
          {/* PANEL LATERAL */}

          {/* PANEL DE FILTROS COLAPSABLE */}
          {sidebarOpen && (
          <div style={{
            width: "280px",           // un poco más ancho para los nuevos filtros
            padding: "10px",
            borderRight: "1px solid #ddd",
            background: "#fafafa",
            overflowY: "auto",
            maxHeight: "650px"
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Filtros</h5>
            </div>

            <Accordion defaultActiveKey={["0"]} alwaysOpen>
              
              {/* 1. Provincias */}
              <Accordion.Item eventKey="0">
                <Accordion.Header>Provincias</Accordion.Header>
                <Accordion.Body>
                  {provincias.map(p => (
                    <Form.Check
                      key={p.codprov}
                      type="checkbox"
                      label={p.nomprov}
                      checked={provinciasSeleccionadas.includes(p.codprov)}
                      onChange={() => toggleProvincia(p.codprov)}
                      className="mb-1"
                    />
                  ))}
                </Accordion.Body>
              </Accordion.Item>

              {/* 2. Rango Salarial */}
              <Accordion.Item eventKey="1">
                <Accordion.Header>Rango Salarial</Accordion.Header>
                <Accordion.Body>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Form.Label className="small">Mínimo</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="0"
                        value={rangoSueldoMin}
                        onChange={(e) => setRangoSueldoMin(e.target.value ? Number(e.target.value) : "")}
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small">Máximo</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="99999"
                        value={rangoSueldoMax}
                        onChange={(e) => setRangoSueldoMax(e.target.value ? Number(e.target.value) : "")}
                      />
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>

              {/* 3. Sexo */}
              <Accordion.Item eventKey="2">
                <Accordion.Header>Sexo</Accordion.Header>
                <Accordion.Body>
                  {["M", "F"].map(sexo => (
                    <Form.Check
                      key={sexo}
                      type="checkbox"
                      label={sexo === "M" ? "Masculino" : "Femenino"}
                      checked={sexosSeleccionados.includes(sexo)}
                      onChange={() => {
                        setSexosSeleccionados(prev =>
                          prev.includes(sexo)
                            ? prev.filter(s => s !== sexo)
                            : [...prev, sexo]
                        );
                      }}
                    />
                  ))}
                </Accordion.Body>
              </Accordion.Item>

            </Accordion>
          </div>
          )}

          {/* MAPA */}
          <div style={{ flex: 1 }}>
            <MapContainer
              center={[-2.17, -79.9]}
              zoom={12}
              style={{ height: "650px", width: "100%" }}
            >
              <MapRefSetter mapRef={mapRef} />

              <Pane name="polygons" style={{ zIndex: 300 }} />
              <Pane name="labels" style={{ zIndex: 400, pointerEvents: "none" }} />
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              {!loadingPoligonos &&
                poligonos.map((p: any) => {
                  if (!p.geometry) return null;
                  const geo = cleanGeometry(JSON.parse(p.geometry));

                  const zonaKey = `${p.codparroquia}-${p.codzona}`;
                  const isSelected = selectedZones.includes(zonaKey);

                  return (
                    <GeoJSON
                      key={`${p.id}-${isSelected}`}
                      pane="polygons"
                      data={geo}
                      style={{
                        color: isSelected ? "#000" : getColorByDensity(p.contactos),
                        weight: isSelected ? 5 : 1.8,
                        fillColor: getColorByDensity(p.contactos),
                        fillOpacity: isSelected ? 0.75 : 0.4,
                      }}
                      eventHandlers={{
                        click: () => toggleSelect(p.codzona, p.codparroquia)
                      }}
                    >
                      <Tooltip sticky direction="top" offset={[0, -10]}>
                        <strong>{p.nomzona}</strong><br />
                        Contactos: <b>{p.contactos}</b><br />
                        Teléfonos: {p.telefonos} | Correos: {p.correos}
                      </Tooltip>
                    </GeoJSON>                  
                  );
                })}
            </MapContainer>
          </div>
        </div>        
      </Card>

      {/* TABLA RESUMEN */}
      <Card className="mb-4">
        <Card.Header><strong>Resumen por Cluster</strong></Card.Header>

        {loadingResumen && (
          <div className="text-center py-3">Cargando resumen...</div>
        )}

        {!loadingResumen && resumen.length === 0 && (
          <div className="text-center py-3 text-muted">
            Selecciona uno o más clusters en el mapa
          </div>
        )}

        {!loadingResumen && resumen.length > 0 && (
          <>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Cantón</th>
                  <th>Parroquia</th>
                  <th>Cluster</th>
                  <th>Total contactos</th>
                  <th>Total telefonos</th>
                  <th>Total correos</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map((r: any) => {

                  //const isSelected = clusterSeleccionadoTabla?.id === r.id;

                  return (
                    <tr
                      key={r.id}
                      onClick={() => setClusterSeleccionadoTabla({
                        id: r.id,
                        codzona: r.codzona,
                        codparroquia: r.codparroquia
                      })}
                      style={{
                        cursor: "pointer",
                        backgroundColor: clusterSeleccionadoTabla?.codzona === r.codzona ? "#e3f2fd" : "transparent",
                        fontWeight: clusterSeleccionadoTabla?.codzona === r.codzona ? "bold" : "normal"
                      }}
                      className="hover-row"
                    >
                      <td>{r.nomcanton}</td>
                      <td>{r.nomparroquia}</td>
                      <td>{r.nomzona}</td>
                      <td className="text-end">{formatNumber(r.total_contactos)}</td>
                      <td className="text-end">{formatNumber(r.total_telefonos)}</td>
                      <td className="text-end">{formatNumber(r.total_correos)}</td>
                    </tr>
                )})}

                {/* === FILA DE TOTALES === */}
                <tr className="table-success fw-bold">
                  <td colSpan={3} className="text-end fw-bold">TOTAL GENERAL</td>
                  <td className="text-end fw-bold">{formatNumber(
                    resumen.reduce((sum, r) => sum + (r.total_contactos || 0), 0)
                  )}</td>
                  <td className="text-end fw-bold">{formatNumber(
                    resumen.reduce((sum, r) => sum + (r.total_telefonos || 0), 0)
                  )}</td>
                  <td className="text-end fw-bold">{formatNumber(
                    resumen.reduce((sum, r) => sum + (r.total_correos || 0), 0)
                  )}</td>
                </tr>                
              </tbody>
            </Table>
            <div className="d-flex justify-content-between p-3">
              <Button
                variant="outline-primary"
                disabled={pageRes === 1}
                onClick={() => setPageRes(pageRes - 1)}
              >
                ← Anterior
              </Button>

              <span>Página {pageRes}</span>

              <Button
                variant="outline-primary"
                onClick={() => setPageRes(pageRes + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* TABLA DETALLE */}
      <Card>
        <Card.Header>
          <strong>Detalle de Contactos</strong>
          {totalDetalle > 0 && (
            <span className="ms-2 text-muted">({totalDetalle} contactos encontrados)</span>
          )}
        </Card.Header>

        {loadingDetalle && (
          <div className="text-center py-3">Cargando contactos...</div>
        )}

        {!loadingDetalle && detalle.length === 0 && (
          <div className="text-center py-3 text-muted">
            Selecciona uno o más clusters para ver el detalle
          </div>
        )}

        {!loadingDetalle && detalle.length > 0 && (          
          <>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Cédula</th>
                  <th>Teléfono</th>
                  <th>Correo</th>
                  <th>Empresa</th>
                  <th>Ocupación</th>                  
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {detalle.map((c: any) => (                   
                  <tr 
                    key={c.id}
                  >
                    <td>{c.apenomafi}</td>                    
                    <td>{c.numafi}</td>
                    <td>{c.celular}</td>
                    <td>{c.email}</td>
                    <td>{c.nomempresa}</td>
                    <td>{c.ocuafi}</td>
                    <td>
                      <Button variant="info" size="sm" className="me-2" onClick={() => handleDetails(c.id)}>Detalles</Button>
                    </td>                    
                  </tr>
                ))}
              </tbody>
            </Table>

            <Pagination>
              <Pagination.First onClick={() => setPageDet(1)} disabled={pageDet === 1 || loadingDetalle} />
              <Pagination.Prev onClick={() => setPageDet(p => Math.max(p - 1, 1))} disabled={pageDet === 1 || loadingDetalle} />

              {getPaginationItems().map((item, index) =>
                item === 'ellipsis' ? (
                  <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                ) : (
                  <Pagination.Item
                    key={item}
                    active={item === pageDet}
                    onClick={() => setPageDet(item)}
                    disabled={loadingDetalle}
                  >
                    {item}
                  </Pagination.Item>
                )
              )}

              <Pagination.Next onClick={() => setPageDet(p => Math.min(p + 1, totalPages))} disabled={pageDet === totalPages || loadingDetalle} />
              <Pagination.Last onClick={() => setPageDet(totalPages)} disabled={pageDet === totalPages || loadingDetalle} />
            </Pagination>
          </>
        )}
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg"> 
        <Modal.Header closeButton>
          <Modal.Title className="h5">Detalles del contacto</Modal.Title>
        </Modal.Header>
        
        {/* Cuerpo de la modal compacto y con campos en línea */}
        <Modal.Body className="small-modal-body"> 
          {selectedContact ? (
            <Form>
              
              {/* 1. Nombre (Apenomafi) - Editable y requerido */}
              <RenderInlineField
                label="Nombre"
                field="apenomafi"
                contact={selectedContact}
                setContact={setSelectedContact}                
                readOnly
              />

              <RenderInlineField
                label="Cédula"
                field="numafi"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Teléfono"
                field="celular"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />
              
              <RenderInlineField
                label="Correo"
                field="email"
                type="email"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />
              
              <RenderInlineField
                label="Dirección"
                field="dirafi"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />
              
              <RenderInlineField
                label="Ocupación"
                field="ocuafi"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />
                            
              <RenderInlineField
                label="Sueldo"
                field="sueldo"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Empresa"
                field="nomempresa"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Ruc Empresa"
                field="rucemp"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Dirección Empresa"
                field="dirsuc"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Tipo Empresa"
                field="tipoempresa"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

              <RenderInlineField
                label="Teléfono Empresa"
                field="telsuc"
                contact={selectedContact}
                setContact={setSelectedContact}
                readOnly
              />

            </Form>
          ) : (
            <p>Cargando...</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={dialogGuardar} onHide={() => setDialogGuardar(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Actualizar Cluster" : "Guardar como Nuevo"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nombre del cluster</Form.Label>
            <Form.Control
              type="text"
              value={nombreCluster}
              onChange={(e) => setNombreCluster(e.target.value)}
              placeholder="Nombre del cluster"
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDialogGuardar(false)}>
            Cancelar
          </Button>
          <Button variant="success" onClick={() => guardarCluster(true)}>
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>

<Modal 
  show={dialogAbrir} 
  onHide={() => setDialogAbrir(false)} 
  centered 
  size="lg"
>
  <Modal.Header closeButton>
    <Modal.Title>Clusters Guardados</Modal.Title>
  </Modal.Header>
  
  <Modal.Body>
    <Form.Control
      type="text"
      placeholder="Buscar por nombre..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      className="mb-3"
    />

    <Form.Check 
      type="checkbox"
      label="Mostrar clusters inactivos"
      checked={mostrarInactivos}
      onChange={(e) => {
        setMostrarInactivos(e.target.checked);
        setTimeout(cargarListaClusters, 150);
      }}
      className="mb-3"
    />

    <ListGroup style={{ maxHeight: "55vh", overflowY: "auto" }}>
      {listaClusters
        .filter(c => c.nombre.toLowerCase().includes(searchTerm.toLowerCase()))
        .map((c) => (
          <ListGroup.Item key={c.id} className="py-3">
            <div className="d-flex justify-content-between">
              <div style={{ cursor: "pointer" }} onClick={() => abrirCluster(c)}>
                <strong>{c.nombre}</strong>
                {c.descripcion && <small className="text-muted d-block">{c.descripcion}</small>}
                
                {/* Mostrar creador si no soy el dueño */}
                {c.creador_id !== user.user.id && (
                  <small className="text-muted d-block">Creado por: {c.creador_nombre}</small>
                )}

                {/* Lista de compartidos (solo si soy el dueño) */}
                {c.creador_id === user.user.id && c.compartido_con && c.compartido_con.length > 0 && (
                <div className="mt-2">
                    <small className="text-muted d-block mb-1">Compartido con:</small>
                    <div className="d-flex flex-wrap gap-2">
                      {c.compartido_con.map((shared: any) => {
                        return (
                          <div
                            key={shared.usuario_id}
                            className="d-flex align-items-center gap-2"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              if (window.confirm(`¿Quitar acceso a ${shared.username}?`)) {
                                quitarCompartir(c.id, shared.usuario_id);
                              }
                            }}
                            title={`${shared.username} - ${shared.permiso.toUpperCase()}`}
                          >
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                              style={{
                                width: "34px",
                                height: "34px",
                                backgroundColor: shared.permiso === "edit" ? "#198754" : "#0dcaf0", // verde o cyan
                                fontSize: "13px",
                                border: "2px solid white"
                              }}
                              title={`${shared.username} (${shared.permiso.toUpperCase()})`}
                            >
                              {getInitials(shared.username)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Botones de acción */}
              <div className="d-flex gap-2 align-items-center">
                {/* Estado */}
                {c.creador_id === user.user.id && (
                  <Button
                    variant={c.activo ? "outline-danger" : "outline-success"}
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); toggleClusterStatus(c.id, c.nombre); }}
                    title={c.activo ? "Desactivar" : "Activar"}
                  >
                    <i className={`bi ${c.activo ? 'bi-toggle-off' : 'bi-toggle-on'}`}></i>
                  </Button>
                )}

                {/* Compartir */}
                {c.creador_id === user.user.id && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); abrirModalCompartir(c); }}
                    title="Compartir"
                  >
                    <i className="bi bi-share-fill"></i>
                  </Button>
                )}
              </div>
            </div>
          </ListGroup.Item>
        ))}
    </ListGroup>
  </Modal.Body>

  <Modal.Footer>
    <Button variant="secondary" onClick={() => setDialogAbrir(false)}>
      Cerrar
    </Button>
  </Modal.Footer>
</Modal>


{/* Modal de Confirmación */}
<Modal 
  show={showConfirmModal} 
  onHide={() => setShowConfirmModal(false)} 
  centered
>
  <Modal.Header closeButton>
    <Modal.Title>Confirmar acción</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {confirmAction?.message}
  </Modal.Body>
  <Modal.Footer>
    <Button 
      variant="secondary" 
      onClick={() => setShowConfirmModal(false)}
    >
      Cancelar
    </Button>
    <Button 
      variant={confirmAction?.type === 'toggle' ? "warning" : "danger"} 
      onClick={async () => {
        if (!confirmAction) return;
        
        setShowConfirmModal(false);

        try {
          if (confirmAction.type === 'toggle' && confirmAction.clusterId) {
            const res = await api.post<{ 
              ok: boolean; 
              activo: boolean; 
              message: string 
            }>(`${API_URL}/geo/cluster/toggle-status/${confirmAction.clusterId}`);
            
            alert(res.data.message); // Temporal - luego podemos mejorarlo
            cargarListaClusters();
          }
        } catch (error: any) {
          alert("Error: " + (error.response?.data?.detail || error.message));
        }
      }}
    >
      Confirmar
    </Button>
  </Modal.Footer>
</Modal>

{/* Modal Compartir con Autocompletado */}
<Modal show={showShareModal} onHide={() => {
  setShowShareModal(false);
  setSearchUserTerm("");
  setUserSuggestions([]);
}} centered>
  <Modal.Header closeButton>
    <Modal.Title>Compartir Cluster</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <p><strong>{clusterToShare?.nombre}</strong></p>

    <Form.Group className="mb-3">
      <Form.Label>Buscar usuario</Form.Label>
      <Form.Control
        type="text"
        placeholder="Escribe nombre o username..."
        value={searchUserTerm}
        onChange={async (e) => {
          const term = e.target.value;
          setSearchUserTerm(term);
          
          if (term.length >= 2) {
            try {
              const res = await api.get<User[]>('/auth/users/search', { params: { q: term } });
              setUserSuggestions(res.data);
            } catch (err) {
              console.error(err);
            }
          } else {
            setUserSuggestions([]);
          }
        }}
      />

      {/* Lista de sugerencias */}
      {userSuggestions.length > 0 && (
        <ListGroup className="mt-1" style={{ maxHeight: "200px", overflowY: "auto" }}>
          {userSuggestions.map((user: any) => (
            <ListGroup.Item 
              key={user.id}
              action
              onClick={() => {
                setSearchUserTerm(user.username);
                setUsuarioDestinoId(user.id.toString());
                setUserSuggestions([]);
              }}
            >
              <strong>{user.username}</strong> 
              {user.full_name && <span className="text-muted"> - {user.full_name}</span>}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </Form.Group>

    <Form.Group className="mb-3">
      <Form.Label>Rol</Form.Label>
      <div className="d-flex gap-4">
        <Form.Check
          type="radio"
          label="Solo Ver (View)"
          name="permiso"
          checked={permisoSeleccionado === "view"}
          onChange={() => setPermisoSeleccionado("view")}
        />
        <Form.Check
          type="radio"
          label="Editar (Edit)"
          name="permiso"
          checked={permisoSeleccionado === "edit"}
          onChange={() => setPermisoSeleccionado("edit")}
        />
      </div>
    </Form.Group>
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowShareModal(false)}>
      Cancelar
    </Button>
    <Button 
      variant="primary" 
      onClick={compartirCluster}
      disabled={!usuarioDestinoId}
    >
      Compartir
    </Button>
  </Modal.Footer>
</Modal>

      <Modal show={showListmonk} onHide={() => setShowListmonk(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Enviar a Listmonk</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Select value={listaId} onChange={(e) => setListaId(Number(e.target.value))}>
            <option value="">Selecciona una lista</option>
            {listas.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowListmonk(false)}>Cancelar</Button>
          <Button variant="primary" disabled={!listaId || exportando} onClick={exportarAListmonk}>
            {exportando ? "Enviando..." : "Enviar"}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}
